import * as React from 'react'

/* TYPES */

interface ListNavigationConfig {
  circular?: boolean
  initialIndex?: number
  size?: number
}

interface ListNavigationApi {
  clear: () => void
  decrement: () => void
  increment: () => void
  index: number
  reset: () => void
  set: (index: number) => void
}

interface Props extends ListNavigationConfig {
  children?: React.ReactNode
}

interface RenderProps extends ListNavigationConfig {
  children: (props: ListNavigationApi) => {}
}

const ident: (x: number) => number = x => x

// restricts an index to [0, size]
const clamp: (max: number) => (n: number) => number = max => n =>
  Math.min(max - 1, Math.max(0, n))

const wrap: (max: number) => (n: number) => number = max => n =>
  n < 0 ? max + n : n % max

const handleSize: (circular: boolean, size: number) => (n: number) => number = (
  circular,
  size
) => (size ? (circular ? wrap(size) : clamp(size)) : ident)

/* HOOK */

export const useListNavigation = ({
  circular = false,
  initialIndex = -1,
  size = 0,
}: ListNavigationConfig): ListNavigationApi => {
  const [index, set] = React.useState(initialIndex)

  if (circular && (!size || size < 0)) {
    console.warn(
      'You must provide a non-negative integer `size` prop if you are going to use the `circular` prop'
    )
  }

  const next = handleSize(circular, size)

  const clear: () => void = () => set(0)
  const increment: () => void = () => set(next(index + 1))
  const decrement: () => void = () => set(next(index - 1))
  const reset: () => void = () => set(initialIndex)

  React.useEffect(reset, [size])

  return {
    clear,
    decrement,
    increment,
    index,
    reset,
    set,
  }
}

/* DECORATOR */

export const withListNavigation = <T extends {}>(
  config: ListNavigationConfig
) => (Component: React.ComponentType<T>) => (
  props: T & { listNavigation: ListNavigationConfig }
) => (
  <Component
    {...props}
    listNavigation={useListNavigation({ ...config, ...props.listNavigation })}
  />
)

/* RENDER PROP */

export const ListNavigation = ({ children, ...props }: RenderProps) => {
  const listNavigation = useListNavigation(props)

  return typeof children === 'function' ? children(listNavigation) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<ListNavigationProvider>\``
  )

export const ListNavigationContext = React.createContext<ListNavigationApi>({
  clear: noop('clear'),
  decrement: noop('decrement'),
  increment: noop('increment'),
  index: -1,
  reset: noop('reset'),
  set: noop('set'),
})

export const ListNavigationProvider = ({ children, ...props }: Props) => (
  <ListNavigationContext.Provider value={useListNavigation(props)}>
    {children}
  </ListNavigationContext.Provider>
)

export const consumeListNavigation = () =>
  React.useContext(ListNavigationContext)
